import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CaptchaError,
  Input,
  Label,
  useCaptcha,
} from '@nuberu/ui';
import { useState, type FormEvent, type ReactElement } from 'react';

import { getSupabaseClient } from '@/lib/supabase';

// Password sign-in for the backoffice. Platform admins already have a Supabase
// account, so email + password is the simplest reliable path (no email
// deliverability dependency for a single-operator tool). On success, the
// useSession subscription swaps the view — no navigation needed here.
const CAPTCHA_FAILURE =
  'No hemos podido comprobar que eres una persona. Prueba otra vez; si sigue pasando, desactiva el bloqueador de anuncios o usa otro navegador.';

const siteKeyFromEnv = (): string | undefined => {
  const key: unknown = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  return typeof key === 'string' ? key : undefined;
};

export const LoginScreen = (): ReactElement => {
  const captcha = useCaptcha(siteKeyFromEnv());
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (): Promise<void> => {
    setError(null);
    setSubmitting(true);
    try {
      const captchaToken = await captcha.getToken();
      const { error: signInError } = await getSupabaseClient().auth.signInWithPassword({
        email: email.trim(),
        password,
        ...(captchaToken !== undefined ? { options: { captchaToken } } : {}),
      });
      if (signInError?.code === 'captcha_failed') {
        setError(CAPTCHA_FAILURE);
      } else if (signInError) {
        // Deliberately vague — never confirm whether an email exists.
        setError('No pudimos iniciar sesión. Revisa el correo y la contraseña.');
      }
    } catch (err) {
      setError(err instanceof CaptchaError ? CAPTCHA_FAILURE : 'Error de red. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void submit();
  };

  return (
    <div className="grid min-h-screen place-items-center bg-surface-2 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Nuberu · Backoffice</CardTitle>
          <CardDescription>Acceso restringido al equipo de la plataforma.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="flex flex-col gap-4" onSubmit={onSubmit}>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">Correo</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {error !== null ? (
              <p className="text-sm text-status-warning" role="alert">
                {error}
              </p>
            ) : null}
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Entrando…' : 'Entrar'}
            </Button>
            {captcha.field}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
